import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CarouselArrow } from '../../components/CarouselArrow/CarouselArrow';
import { CarouselTrack } from '../../components/Carousel/CarouselTrack';
import { useInView } from '../../hooks/useInView';
import { AthleteCard } from './AthleteCard';
import type { Community } from './types';

import './CommunitySection.css';

type CommunitySectionProps = {
    data: Community;
};

// Track gap between cards; added to the measured card width for one scroll step.
const TRACK_GAP = 16;
const FALLBACK_CARD_WIDTH = 300;

export function CommunitySection({ data }: CommunitySectionProps) {
    const [ref, inView] = useInView<HTMLElement>();
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const syncArrows = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) { return; }
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanPrev(el.scrollLeft > 1);
        setCanNext(el.scrollLeft < maxScroll - 1);
    }, []);

    useEffect(() => {
        syncArrows();
        const el = scrollerRef.current;
        if (!el) { return undefined; }
        el.addEventListener('scroll', syncArrows, { passive: true });
        window.addEventListener('resize', syncArrows);
        return () => {
            el.removeEventListener('scroll', syncArrows);
            window.removeEventListener('resize', syncArrows);
        };
    }, [syncArrows]);

    const scrollByStep = (direction: 1 | -1) => {
        const el = scrollerRef.current;
        if (!el) { return; }
        // Measure the rendered card so the step stays one card wide as cards
        // shrink with the viewport.
        const card = el.querySelector<HTMLElement>('.programs-card');
        const cardWidth = card ? card.getBoundingClientRect().width : FALLBACK_CARD_WIDTH;
        el.scrollBy({ left: direction * (cardWidth + TRACK_GAP), behavior: 'smooth' });
    };

    // Only relevant on desktop; mobile navigates by swipe (arrows are hidden in CSS).
    const overflowing = canPrev || canNext;

    return (
        <section
            ref={ref}
            className={[
                'programs-community',
                'faves-reveal-group',
                inView ? 'faves-reveal-group--in' : '',
                overflowing ? 'programs-community--scrollable' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className="programs-community__head">
                <div className="programs-community__intro">
                    <span className="programs-eyebrow faves-reveal">{data.eyebrow}</span>
                    <h2
                        className="programs-community__heading faves-reveal"
                        style={{ '--reveal-delay': '120ms' } as React.CSSProperties}
                    >
                        {data.heading}
                    </h2>
                </div>
                {overflowing && (
                    <div className="programs-community__arrows">
                        <CarouselArrow
                            direction="prev"
                            ariaLabel="Previous athletes"
                            onClick={() => scrollByStep(-1)}
                            disabled={!canPrev}
                        />
                        <CarouselArrow
                            direction="next"
                            ariaLabel="Next athletes"
                            onClick={() => scrollByStep(1)}
                            disabled={!canNext}
                        />
                    </div>
                )}
            </div>
            <CarouselTrack ref={scrollerRef} className="programs-community__scroller">
                <div className="programs-community__track">
                    {data.athletes.map((athlete, index) => (
                        <AthleteCard
                            key={athlete.name}
                            data={athlete}
                            style={{ '--reveal-delay': `${index * 90}ms` } as React.CSSProperties}
                        />
                    ))}
                </div>
            </CarouselTrack>
            {data.swipeHint && <span className="programs-community__swipe">{data.swipeHint}</span>}
        </section>
    );
}
